/**
 * Оси вариантов сверх цвета и размера (fd-catalog-07): материал и свой
 * параметр продавца. Значение оси у строки - `variant.options[key]`, ось -
 * подпись этого ключа (`product.variantAxes`). Покупатель выбирает значение
 * чипом под размерами, выбор вместе с размером указывает на одну строку.
 */
export type AxisVariant = {
	id: string;
	size: string;
	stock: number;
	options?: Record<string, string>;
};
export type AxisPicks = Record<string, string>;

const valueOf = (variant: AxisVariant, key: string) => (variant.options?.[key] ?? "").trim();

/** Значения оси в порядке первого появления, без пустых. */
export const axisValues = (variants: AxisVariant[], key: string): string[] => [
	...new Set(variants.map((variant) => valueOf(variant, key)).filter(Boolean)),
];

/** Строка подходит под выбранные значения осей (невыбранная ось не мешает). */
export const matchesPicks = (variant: AxisVariant, picks: AxisPicks): boolean =>
	Object.entries(picks).every(([key, value]) => !value || valueOf(variant, key) === value);

/** Строка под выбор: размер (если выбран) и значения осей; в наличии - первой. */
export const resolveAxisPick = <V extends AxisVariant>(
	variants: V[],
	pick: { size?: string; picks: AxisPicks },
): V | undefined => {
	const fits = variants.filter((variant) => (!pick.size || variant.size === pick.size) && matchesPicks(variant, pick.picks));
	return fits.find((variant) => variant.stock > 0) ?? fits[0];
};

/** Значение оси можно выбрать: есть строка в наличии с ним при остальном выборе. */
export const axisValueAvailable = (
	variants: AxisVariant[],
	key: string,
	value: string,
	pick: { size?: string; picks: AxisPicks },
): boolean => {
	const { [key]: _own, ...rest } = pick.picks;
	return variants.some(
		(variant) =>
			variant.stock > 0 &&
			valueOf(variant, key) === value &&
			(!pick.size || variant.size === pick.size) &&
			matchesPicks(variant, rest),
	);
};

/**
 * Начальный выбор: ось с одним значением выбрана сразу; при переходе по
 * ссылке на конкретную строку (`?variant=`) - её значения.
 */
export const initialAxisPicks = (
	axes: { key: string }[],
	variants: AxisVariant[],
	fromLink?: AxisVariant,
): AxisPicks => {
	const out: AxisPicks = {};
	for (const axis of axes) {
		const values = axisValues(variants, axis.key);
		const linked = fromLink ? valueOf(fromLink, axis.key) : "";
		if (values.length === 1) out[axis.key] = values[0] as string;
		else if (linked && values.includes(linked)) out[axis.key] = linked;
	}
	return out;
};

/** Первая ось, где выбрать есть из чего, а выбора нет. */
export const missingAxis = <A extends { key: string }>(
	axes: A[],
	variants: AxisVariant[],
	picks: AxisPicks,
): A | undefined => axes.find((axis) => axisValues(variants, axis.key).length > 1 && !picks[axis.key]);
